<script lang="ts" setup>
import Button from 'primevue/button'
import Message from 'primevue/message'
import type { StockShortage } from '~/server/utils/stockShortages'

const { t } = useI18n()
const nuxtApp = useNuxtApp()

const asyncOperation = ref(false)
const buuid = useCookie('buuid')
const user = useSupabaseUser()
const commerceStore = useCommerceStore()
const { clearCart, clearDetails } = commerceStore
const {
  cart,
  cartTotal,
  details,
  itemsInCart,
  openCart,
  selectedDiscount,
  selectedPaymentMethod,
} = storeToRefs(commerceStore)
// Counter order rejected for short stock: list + quick count, then retry
const shortages = ref<StockShortage[]>([])
const showShortages = ref(false)
const canAdjustShortages = ref(true)

const shiftStore = useShiftStore()
const {
  shiftId,
  shiftStatus,
} = storeToRefs(shiftStore)

async function generateNewOrder() {
  let keepCartOpen = false
  try {
    asyncOperation.value = true

    let response
    if (commerceStore.isEditingOrder && commerceStore.editingOrderId) {
      console.log('editing order...')
      // Transform cart data to format expected by update API
      const transformedCart = cart.value.map((item: any) => {
        if (item.type === 'PRODUCT') {
          return {
            ...item,
            items: [{
              ...item.items?.[0],
              // Transform modifiers from cart format to API format
              selected_modifiers: item.modifiers?.length > 0
                ? item.modifiers.reduce((acc: any, modifier: any) => {
                    acc[modifier.group_id] = modifier.selected || []
                    return acc
                  }, {})
                : item.items?.[0]?.selected_modifiers || {},
            }],
          }
        }
        else if (item.type === 'COMBO' || item.type === 'CUSTOM_COMBO') {
          return {
            ...item,
            items: item.items?.map((subItem: any) => ({
              ...subItem,
              // Transform modifiers from cart format to API format for combo items
              selected_modifiers: subItem.modifiers?.length > 0
                ? subItem.modifiers.reduce((acc: any, modifier: any) => {
                    acc[modifier.group_id] = modifier.selected || []
                    return acc
                  }, {})
                : subItem.selected_modifiers || {},
            })) || [],
          }
        }
        return item
      })

      // Update existing order
      response = await $fetch(`/api/business/${buuid.value}/orders/${commerceStore.editingOrderId}`, {
        method: 'PUT',
        body: {
          order_total: cartTotal.value,
          client_details: details.value,
          payment_details: selectedPaymentMethod.value ?? null,
          discount: selectedDiscount.value?.percentage ?? null,
          notes: null, // Assuming notes are not part of this flow, or need to be added
          cart: transformedCart,
        },
      })
      useSuccessToast('Order updated successfully')
    }
    else {
      // Create new order
      console.log('creating order...')
      response = await $fetch(`/api/business/${buuid.value}/order`, {
        method: 'POST',
        body: {
          cart: cart.value,
          cartTotal: cartTotal.value,
          details: details.value,
          payment_details: selectedPaymentMethod.value ?? null,
          discount: selectedDiscount.value?.percentage ?? null,
          shift_id: shiftId.value,
        },
      })
      useSuccessToast('Order generated successfully')
    }

    if (response?.success || response?.orderId) {
      // Capture order ID before resetting editing state
      const orderIdToNavigate = commerceStore.editingOrderId || response.orderId
      clearCart()
      clearDetails()
      commerceStore.details.delivery = false

      // Reset editing state after successful order operation
      if (commerceStore.isEditingOrder) {
        commerceStore.cancelOrderEdit()
      }

      asyncOperation.value = false

      if ('tappyPrint' in window && typeof (window as { tappyPrint?: (url: string) => void }).tappyPrint === 'function') {
        ;(window as { tappyPrint: (url: string) => void }).tappyPrint(`/store/pos/orders/${orderIdToNavigate}`)
      }
      else {
        return navigateTo(`/store/pos/orders/order?orderId=${orderIdToNavigate}`)
      }
    }
  }
  catch (e: any) {
    console.error(e)

    const shortList = stockShortagesFromError(e)
    if (shortList) {
      // Keep the cart open: the dialog lives in it and the order is retried as is
      shortages.value = shortList
      canAdjustShortages.value = e?.data?.data?.can_adjust !== false
      showShortages.value = true
      keepCartOpen = true
      return
    }

    // Handle errors (network, stock issues, validation, etc.)
    const rawMessage = e?.data?.message || e?.message

    if (rawMessage) {
      // Try to parse insufficient stock error for translation
      const stockErrorRegex = /Insufficient stock for product (.+?)\. (?:Insufficient stock\. )?Available: (\d+), Required: (\d+)/
      const match = rawMessage.match(stockErrorRegex)

      if (match) {
        const [, productName, available, required] = match
        const translatedMessage = nuxtApp.$i18n.t('Insufficient stock for product {productName}. Available: {available}, Required: {required}', {
          productName,
          available,
          required,
        })
        useErrorToast(translatedMessage, 30000)
      }
      else {
        // Fallback to original message if parsing fails
        useErrorToast(rawMessage, 30000)
      }
    }
    else {
      // Fallback to generic error message
      useErrorToast(nuxtApp.$i18n.t('Error processing order'), 30000)
    }
  }
  finally {
    asyncOperation.value = false
    if (!keepCartOpen) openCart.value = false
  }
}

async function retryAfterAdjust() {
  showShortages.value = false
  await generateNewOrder()
}

const enableGenerateOrder = computed(() => {
  return selectedPaymentMethod.value?.id && itemsInCart.value > 0
})
</script>

<template>
  <div v-if="user">
    <Message
      v-if="shiftStatus === 'closed'"
      severity="warn"
      class="mb-3"
    >
      {{ t('Please open a shift to create an order') }}
    </Message>
    <Message
      v-if="!selectedPaymentMethod?.id && itemsInCart > 0"
      severity="warn"
      class="mb-3"
    >
      {{ t('Please add payment methods') }}
    </Message>
    <Button
      :disabled="!enableGenerateOrder || asyncOperation || shiftStatus === 'closed'"
      :label="commerceStore.isEditingOrder ? t('Save changes') : t('Generate order')"
      icon="pi pi-check-circle"
      severity="primary"
      class="w-full text-base font-semibold py-2 mb-4"
      @click="generateNewOrder"
    />
    <CartStockShortageDialog
      v-model:visible="showShortages"
      :shortages="shortages"
      :cart="cart"
      :can-adjust="canAdjustShortages"
      @adjusted="retryAfterAdjust"
    />
  </div>
</template>
